// frontend/src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // se hai già il token, vai in home
    const existing = localStorage.getItem("access");
    if (existing) {
      const role = (localStorage.getItem("me.role") || "").toUpperCase();
      navigate(role === "CLIENTE" ? "/app" : "/", { replace: true });
    }
  }, [navigate]);

  // Pulizia dei token da localStorage quando la scheda si chiude
  useEffect(() => {
    const onBeforeUnload = () => {
      if (localStorage.getItem("session_only") === "1") {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("me.username");
        localStorage.removeItem("me.role");
        localStorage.removeItem("me.store_name");
        localStorage.removeItem("session_only");
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    const u = (username || "").trim();
    const p = (password || "").trim();
    if (!u || !p) {
      setErr("Inserisci username e password.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ username: u, password: p }),
      });

      let data = null;
      const text = await res.text();
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        // ignore parse error, user-friendly message below
      }

      if (!res.ok) {
        const msg =
          (data && (data.detail || data.error || data.message)) ||
          (text || `Login fallito (HTTP ${res.status}). Controlla le credenziali.`);
        throw new Error(msg);
      }

      if (!data || !data.access) throw new Error("Risposta senza access token.");

      // Salvo SEMPRE su localStorage (la tua App.jsx controlla lì)
      localStorage.setItem("access", data.access);
      if (data.refresh) localStorage.setItem("refresh", data.refresh);

      // Salvo anche su sessionStorage per praticità
      sessionStorage.setItem("access", data.access);
      if (data.refresh) sessionStorage.setItem("refresh", data.refresh);

      // Flag “solo sessione” se Ricordami è OFF (ripuliremo localStorage alla chiusura tab)
      if (!remember) {
        localStorage.setItem("session_only", "1");
      } else {
        localStorage.removeItem("session_only");
      }

      // (opzionale) chi sono
      try {
        const meRes = await fetch(`${API_URL}/api/me/`, {
          headers: { Authorization: `Bearer ${data.access}` },
        });
        if (meRes.ok) {
          const me = await meRes.json();
          localStorage.setItem("me.username", me.username || "");
          localStorage.setItem("me.role", me.role || "");
          localStorage.setItem("me.store_name", me.store_name || "");

          if (me.must_change_password) {
            window.dispatchEvent(new Event("localstorage-auth"));
            navigate("/force-change-password", { replace: true });
            return;
          }

          if ((me.role || "").toUpperCase() === "CLIENTE") {
            window.dispatchEvent(new Event("localstorage-auth"));
            navigate("/app", { replace: true });
            return;
          }
        }
      } catch {
        // non bloccare il login se /api/me/ fallisce
      }

      // vai in home
      navigate("/", { replace: true });
      // In molte tue pagine lo stato auth è letto da App via localStorage patch,
      // ma forziamo anche un evento per sicurezza:
      window.dispatchEvent(new Event("localstorage-auth"));
    } catch (e2) {
      console.error(e2);
      const msg = e2?.message || "Errore imprevisto.";
      setErr(
        msg.includes("Failed to fetch")
          ? `Impossibile contattare il backend (${API_URL}).`
          : msg
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 420, marginTop: 80 }}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h4 className="mb-3 text-center">Accedi</h4>

          {err && <div className="alert alert-danger">{err}</div>}

          <form onSubmit={handleLogin}>
            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                autoFocus
                className="form-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="es. roberto.desimone"
                autoComplete="username"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <div className="form-check mb-3">
              <input
                id="remember"
                type="checkbox"
                className="form-check-input"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <label htmlFor="remember" className="form-check-label">
                Ricordami
              </label>
            </div>

            <div className="mb-3 text-end">
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ color: "#8b0d12", textDecoration: "none", fontWeight: 600 }}
                onClick={() => navigate("/forgot-password")}
              >
                Password dimenticata?
              </button>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                Recupera l’accesso in pochi secondi
              </div>
            </div>

            <button className="btn btn-primary w-100" disabled={loading}>
              {loading ? "Accesso..." : "Entra"}
            </button>
          </form>

          <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
            Backend: <code>{API_URL}</code>
          </p>
        </div>
      </div>
    </div>
  );
}


